// English source strings for module 11: temporary credentials, access-control connectors, recovery
// queue, access events and badge templates (ADR-018, credentials stream). Arabic: ar-credentials.ts.
export const enCredentials = {
  // ------------------------------------------------------------ API messages
  'cred.api.issueBlocked': 'This credential cannot be requested now. Review the listed blockers.',
  'cred.api.credentialExists': 'This person (or this card) already has a credential that may give access. Resolve it before requesting another.',
  'cred.api.notLive': 'This credential no longer gives access, so there is nothing to revoke.',
  'cred.api.notRetryable': 'Only a failed credential with no revocation request can be requested again.',
  'cred.api.operationResolved': 'This item has already been resolved.',
  'cred.api.notUnknown': 'Read-back applies only to an operation whose outcome is unknown.',
  'cred.api.readBackUnsupported': 'This connector cannot read back credentials. Check the access-control system directly and record what you found.',
  'cred.api.readBackRunning': 'A read-back is in progress for this item. Wait for its result.',
  'cred.api.decisionNotApplicable': 'That decision does not apply to this operation in its current state.',
  'cred.api.revokeOpen': 'A revocation is already open for this credential.',
  'cred.api.connectorExists': 'This site already has an active access connector. Disable it first.',
  'cred.api.mappingRetired': 'This mapping is already retired.',
  'cred.api.simulatorOnly': 'Simulated events can be sent only to the development simulator.',
  'cred.api.templateNotDraft': 'Only a draft template can be changed. Create a new version instead.',
  'cred.v.adapterUnavailable': 'This platform has no validated adapter yet. It stays unavailable until compatibility tests pass.',
  'cred.v.locationUnknown': 'Choose an active location of this site.',
  'cred.v.mappingExists': 'This location already has an active mapping. Retire it first.',
  'cred.v.credentialUnknown': 'No credential of this connector has that reference.',
  'cred.v.externalRef': 'Use 1–120 letters, digits, spaces or . _ : / -.',
  'cred.v.badgeMandatory': 'A badge always shows the visitor name, the valid-until time and the reference.',
  // ------------------------------------------------------------ workspace (Badges & access)
  'cred.intro': "Physical custody and logical access are separate facts. A departure, a returned card or a provider acknowledgement never confirms that access has ended.",
  'cred.inventorySub': "Returned cards stay held until their access is confirmed inactive.",
  'cred.heldUntilInactive': "Held until access is confirmed inactive",
  'cred.tab.recovery': "Recovery queue",
  'cred.tab.access': "Temporary access",
  'cred.tab.inventory': "Physical inventory",
  'cred.tab.badges': "Badge printing",
  'cred.tab.scope': "Access scope",
  'cred.recovery.title': "Uncertain access operations",
  'cred.recovery.sub': "Read-back confirmation is required before closing the exception.",
  'cred.recovery.empty': "No uncertain or failed access operations.",
  'cred.recovery.note': "Unknown means the access-control system did not confirm the result. Read back the same operation before anything is sent again; observed departure is recorded independently.",
  'cred.col.visitor': "Visitor",
  'cred.col.presence': "Presence",
  'cred.col.credential': "Credential",
  'cred.col.operation': "Operation",
  'cred.col.card': "Card",
  'cred.col.owner': "Owner",
  'cred.col.action': "Action",
  'cred.col.carrier': "Carrier",
  'cred.col.validity': "Valid",
  'cred.col.trigger': "Trigger",
  'cred.col.result': "Result",
  'cred.col.reference': "Operation reference",
  'cred.attempts': { one: "{count} attempt", other: "{count} attempts" },
  'cred.unowned': "Unassigned",
  'cred.claim': "Take ownership",
  'cred.readBack': "Read back",
  'cred.decide': "Record decision",
  'cred.readBackRunning': "Reading back…",
  'cred.operatorHandles': "Platform operator handles recovery",
  'cred.decision.title': "Record a recovery decision",
  'cred.decision.warning': "Record only what you confirmed in the access-control system itself. This decision is audited and cannot be undone.",
  'cred.decision.choose': "Decision",
  'cred.decision.note': "What you checked",
  'cred.decision.noteHint': "For example: “Checked at the access-control console at 14:05 — card disabled.”",
  'cred.decision.record': "Record decision",
  'cred.decision.confirmed_revoked': "Access confirmed removed",
  'cred.decision.retry_revoke': "Send the revocation again (same credential)",
  'cred.decision.not_issued': "Confirmed not issued",
  'cred.list.title': "Temporary credentials",
  'cred.list.sub': "Logical access for visitors, separate from presence and card custody.",
  'cred.list.view': "Show",
  'cred.list.live': "Current and recent",
  'cred.list.all': "All",
  'cred.list.empty': "No credentials yet.",
  'cred.list.issueOnly': "You can request credentials; the register is visible to operators.",
  'cred.issue.open': "Request credential",
  'cred.issue.title': "Request temporary access",
  'cred.issue.sub': "Access is limited to the approved zones and window. Nothing is issued until the access-control system confirms it.",
  'cred.issue.search': "Find a visitor",
  'cred.issue.searchHint': "Name or visit reference (submitted visits in the next 7 days).",
  'cred.issue.noCandidates': "No matching visitors.",
  'cred.issue.change': "Change",
  'cred.issue.token': "Carrier",
  'cred.issue.notAvailable': "not available here",
  'cred.issue.zones': "Zones",
  'cred.issue.zonesHint': "Only zones the visit’s approval covers can be selected. A zone without a mapping blocks the request — it is never replaced by a broader level.",
  'cred.issue.noZones': "The visit has no destination, so no zone is approved.",
  'cred.issue.schedule': "Schedule",
  'cred.issue.blocked': "This request is blocked:",
  'cred.issue.ready': "Ready to request. The result will appear when the access-control system answers.",
  'cred.issue.submit': "Request credential",
  'cred.zone.granted': "mapped",
  'cred.zone.unmapped': "no mapping",
  'cred.zone.not_approved': "not approved",
  'cred.schedule.validity': "Valid",
  'cred.schedule.daily': "Daily window",
  'cred.schedule.siteTime': "Site time zone",
  'cred.schedule.conversion': "Provider conversion",
  'cred.schedule.exact': "Exact",
  'cred.schedule.narrowed': "Narrowed to the provider’s time step (never widened)",
  'cred.schedule.activation': "Activation",
  'cred.scheduledFor': "Scheduled for {at}; eligibility is checked again then.",
  'cred.scheduleProblem.no_remaining_window': "The visit window has ended.",
  'cred.scheduleProblem.mode_unsupported': "The access-control system cannot hold a daily window for several days; it would have to grant round-the-clock access, so the request is refused.",
  'cred.scheduleProblem.too_long': "The window is longer than the access-control system allows.",
  'cred.scheduleProblem.not_representable': "The window cannot be represented exactly and narrowing is not allowed at this site.",
  'cred.scheduleProblem.empty_after_rounding': "Nothing of the window remains after converting to the provider’s time step.",
  'cred.blocker.visit_not_submitted': "The visit is not submitted.",
  'cred.blocker.approval_not_granted': "The visit is not approved.",
  'cred.blocker.participant_removed': "The person was removed from the visit.",
  'cred.blocker.window_ended': "The visit window has ended.",
  'cred.blocker.departed': "The visitor has already left.",
  'cred.blocker.contractor_not_eligible': "Contractor checks are not met.",
  'cred.blocker.credential_exists': "This person already has a credential that may give access.",
  'cred.blocker.prior_unresolved': "An earlier credential’s revocation is still pending or unknown.",
  'cred.blocker.no_connector': "This site has no active access connector.",
  'cred.blocker.visitor_type_no_credential': "This visitor type does not get access credentials.",
  'cred.blocker.token_type_not_allowed': "This carrier is not allowed at this site.",
  'cred.blocker.token_type_unsupported': "The access-control system does not support this carrier.",
  'cred.blocker.card_not_assigned': "Assign a physical card to this visitor first.",
  'cred.blocker.card_in_use': "That card already carries a credential.",
  'cred.blocker.zone_not_approved': "A selected zone is outside the visit’s approval.",
  'cred.blocker.zone_mapping_missing': "A zone has no validated access mapping.",
  'cred.blocker.no_zone': "No zone is selected.",
  'cred.blocker.too_many_zones': "Too many zones for the access-control system.",
  'cred.blocker.schedule_unrepresentable': "The window cannot be represented by the access-control system.",
  'cred.token.card': "Physical card",
  'cred.token.pin': "PIN (provider-issued)",
  'cred.token.qr': "QR (provider-issued)",
  'cred.token.mobile': "Mobile credential",
  'cred.kind.issue': "Issue",
  'cred.kind.revocation': "Revoke",
  'cred.op.queued': "queued",
  'cred.op.dispatched': "sent",
  'cred.op.acknowledged': "acknowledged",
  'cred.op.confirmed': "confirmed",
  'cred.op.failed': "failed",
  'cred.op.unknown': "unknown",
  'cred.op.reconciling': "reading back",
  'cred.trigger.request': "Request",
  'cred.trigger.retry': "Retry",
  'cred.trigger.departure': "Departure",
  'cred.trigger.card_lost': "Card lost",
  'cred.trigger.cancellation': "Visit cancelled",
  'cred.trigger.expiry': "Visit expired",
  'cred.trigger.removal': "Removed from visit",
  'cred.trigger.manual': "Manual request",
  'cred.trigger.reconcile': "Reconciliation",
  'cred.resolution.read_back': "Confirmed by read-back",
  'cred.resolution.manual_confirmed_revoked': "Removal confirmed by a person",
  'cred.resolution.manual_not_issued': "Non-issue confirmed by a person",
  'cred.resolution.superseded': "Superseded",
  'cred.resolution.retried': "Sent again",
  'cred.reason.timeout': "No answer in time",
  'cred.reason.confirmed': "Confirmed",
  'cred.reason.rejected': "Refused by the provider",
  'cred.reason.revoke_rejected': "Revocation refused — access may still exist",
  'cred.reason.scope_mismatch': "Provider confirmed a broader scope than requested",
  'cred.reason.redelivered_after_dispatch': "Interrupted after sending; read back before retrying",
  'cred.reason.read_back_active': "Read-back: active",
  'cred.reason.read_back_absent': "Read-back: not held",
  'cred.reason.read_back_revoked': "Read-back: revoked",
  'cred.reason.read_back_still_active': "Read-back: still active",
  'cred.reason.read_back_unavailable': "Read-back unavailable",
  'cred.reason.read_back_not_supported': "Read-back not supported",
  'cred.reason.adapter_unavailable': "Adapter unavailable",
  'cred.reason.approval_not_granted': "Approval withdrawn before activation",
  'cred.reason.contractor_not_eligible': "Contractor checks failed before activation",
  'cred.reason.revoked_before_activation': "Revoked before activation",
  'cred.reason.request': "Requested",
  'cred.reason.retry': "Requested again",
  'cred.reason.revoke': "Revocation requested",
  'cred.reason.manual_confirmed_revoked': "Removal confirmed by a person",
  'cred.reason.manual_not_issued': "Non-issue confirmed by a person",
  'cred.reason.transport': "Connection failed",
  'cred.reason.credential_changed': "Credential changed meanwhile",
  'cred.reason.zone_mapping_changed': "Zone mapping changed before activation",
  'cred.reason.window_ended': "Window ended before activation",
  'cred.reason.connector_inactive': "Connector disabled",
  'cred.reason.provider_schedule_ended': "Provider schedule ended",
  'cred.reason.nothing_to_revoke': "Nothing left to revoke",
  'cred.reason.credential_not_requested': "No longer requested",
  'cred.reason.visit_not_submitted': "Visit no longer submitted",
  'cred.reason.participant_removed': "Removed from visit",
  'cred.view': "View",
  'cred.retry': "Request again (same credential)",
  'cred.system': "system",
  'cred.detail.title': "Temporary credential",
  'cred.detail.independent': "Each line below is a separate fact. Presence and card custody are shown for context; neither changes the credential.",
  'cred.detail.confirmed': "Confirmed by provider",
  'cred.detail.notConfirmed': "Not confirmed",
  'cred.detail.connector': "Connector",
  'cred.detail.revokeRequested': "Revocation requested",
  'cred.detail.operations': "Provider operations",
  'cred.detail.history': "Lifecycle history",
  'cred.event.state': "State changed",
  'cred.event.issue_queued': "Issue queued",
  'cred.event.revocation_queued': "Revocation queued",
  'cred.event.issue_dispatched': "Issue sent",
  'cred.event.revocation_dispatched': "Revocation sent",
  'cred.event.revoke_requested': "Revocation requested",
  'cred.event.recovery_decision': "Recovery decision",
  'cred.revoke.reason': "Reason for revoking",
  'cred.revoke.hint': "The request goes to the access-control system; the result appears when it answers.",
  'cred.revoke.submit': "Request revocation",
  'cred.events.title': "Access events",
  'cred.events.sub': "Door and zone events reported by the connector, by when they happened. Late events are marked; they never change presence.",
  'cred.events.empty': "No access events.",
  'cred.events.occurred': "Occurred",
  'cred.events.received': "Received",
  'cred.events.late': "Late",
  'cred.events.door': "Door / zone",
  'cred.events.result': "Result",
  'cred.events.correlation': "Correlation",
  'cred.direction.entry': "entry",
  'cred.direction.exit': "exit",
  'cred.direction.unknown': "direction unknown",
  'cred.result.granted': "granted",
  'cred.result.denied': "denied",
  'cred.confidence.exact': "Matched credential",
  'cred.confidence.outside_validity': "Credential, outside its validity",
  'cred.confidence.none': "No matching credential",
  'cred.badge.bandAccess': "Temporary access",
  'cred.badge.bandEscort': "Escort required — not an access card",
  'cred.badge.destination': "Destination",
  'cred.badge.category': "Visitor type",
  'cred.badge.photoPlaceholder': "No photo",
  'cred.badge.qrLabel': "Reference {reference} as a QR code (not an access credential)",
  'cred.badges.title': "Badge templates",
  'cred.badges.sub': "Badge content by site, visitor type and access status. Only approved fields can be printed.",
  'cred.badges.new': "New template",
  'cred.badges.empty': "No templates yet — badges use the built-in layout.",
  'cred.badges.col.name': "Template",
  'cred.badges.col.appliesTo': "Applies to",
  'cred.badges.col.status': "Status",
  'cred.badges.version': "Version {n}",
  'cred.badges.thisSite': "This site",
  'cred.badges.allSites': "All sites",
  'cred.badges.anyCategory': "any visitor type",
  'cred.badges.edit': "Edit",
  'cred.badges.publish': "Publish",
  'cred.badges.discard': "Discard",
  'cred.badges.access.any': "any access status",
  'cred.badges.access.with_credential': "with temporary access",
  'cred.badges.access.without_credential': "without temporary access",
  'cred.badges.status.draft': "draft",
  'cred.badges.status.published': "published",
  'cred.badges.status.retired': "retired",
  'cred.badges.status.discarded': "discarded",
  'cred.badges.note': "Printing never issues an access credential. Reprints keep the badge identity.",
  'cred.badges.designerSub': "Published versions cannot change; publishing replaces the previous version for the same scope.",
  'cred.badges.f.name': "Name",
  'cred.badges.f.scope': "Scope",
  'cred.badges.f.category': "Visitor type code",
  'cred.badges.f.categoryHint': "Leave empty for any visitor type.",
  'cred.badges.f.access': "Access status",
  'cred.badges.f.size': "Size",
  'cred.badges.f.accent': "Accent colour",
  'cred.badges.f.fields': "Fields",
  'cred.badges.f.fieldsHint': "Name, valid-until and reference are always printed. No identity-document numbers, dates of birth or private notes.",
  'cred.badges.f.statusBand': "Show the access status band",
  'cred.badges.size.cr80': "Card (CR80)",
  'cred.badges.size.a6': "Label (A6)",
  'cred.badges.saveDraft': "Save draft",
  'cred.badges.preview': "Preview",
  'cred.badges.sample': "Sample name",
  'cred.badges.sampleLong': "Long Arabic name",
  'cred.badges.sampleShort': "Short name",
  'cred.badges.sampleSite': "Sample site",
  'cred.badges.previewNote': "Long names wrap and shorten; the valid-until time and the QR always stay visible.",
  'cred.badges.field.visitorName': "Visitor name",
  'cred.badges.field.organisation': "Organisation",
  'cred.badges.field.hostName': "Host",
  'cred.badges.field.siteName': "Site",
  'cred.badges.field.destination': "Destination",
  'cred.badges.field.validUntil': "Valid until",
  'cred.badges.field.reference': "Reference",
  'cred.badges.field.category': "Visitor type",
  'cred.badges.field.photo': "Photo (placeholder when missing)",
  'cred.badges.field.referenceQr': "Reference QR (not an access credential)",
  'cred.scope.standalone': "Visits, arrivals and departures work without any access-control system. Connectors only add temporary access.",
  'cred.scope.connector': "Access connector",
  'cred.scope.connectorSub': "The adapter this site uses, its validated capabilities and the site’s credential rules.",
  'cred.scope.none': "No access connector is active at this site.",
  'cred.scope.addSimulator': "Add the development simulator",
  'cred.scope.simulatorName': "Development access simulator",
  'cred.scope.capabilities': "Capabilities",
  'cred.scope.disable': "Disable connector",
  'cred.connector.active': "active",
  'cred.connector.disabled': "disabled",
  'cred.rules.tokens': "Carriers allowed at this site",
  'cred.rules.enclosing': "Include the zones that enclose the destination (building, floor)",
  'cred.rules.narrowing': "Narrow windows the provider cannot hold exactly (never widen)",
  'cred.rules.lead': "Activate this many minutes before the window",
  'cred.rules.expiry': "At visit expiry",
  'cred.rules.expiry.revoke': "Request revocation",
  'cred.rules.expiry.trust': "Accept the provider-confirmed schedule as expiry",
  'cred.rules.timeout': "Provider timeout (ms)",
  'cred.rules.autoReadBack': "Automatic read-back after (s, 0 = off)",
  'cred.sim.title': "Simulator behaviour",
  'cred.sim.hint': "Development only. Lets you see every outcome; no real access-control system is contacted.",
  'cred.sim.issue': "Issue",
  'cred.sim.revoke': "Revoke",
  'cred.sim.readBack': "Read-back",
  'cred.sim.readBack.normal': "available",
  'cred.sim.readBack.unavailable': "unavailable",
  'cred.sim.b.succeed': "succeed",
  'cred.sim.b.reject': "refuse",
  'cred.sim.b.timeout': "time out (not applied)",
  'cred.sim.b.late': "apply, answer too late",
  'cred.sim.b.silent': "never answer",
  'cred.sim.b.broaden': "confirm a broader scope",
  'cred.sim.eventCredential': "Provider credential reference",
  'cred.sim.eventDoor': "Door / access level",
  'cred.sim.sendEvent': "Send simulated event",
  'cred.mapping.title': "Zone mapping",
  'cred.mapping.sub': "Each location maps to one access level of the connector. A zone without a mapping cannot be granted.",
  'cred.mapping.location': "Location",
  'cred.mapping.externalRef': "Access level reference",
  'cred.mapping.add': "Add mapping",
  'cred.mapping.empty': "No mappings yet.",
  'cred.mapping.retire': "Retire",
  'cred.mapping.active': "active",
  'cred.mapping.retired': "retired",
  'cred.catalogue.title': "Connector catalogue",
  'cred.catalogue.sub': "Named platforms stay unavailable until an adapter passes compatibility tests.",
  'cred.catalogue.platform': "Platform",
  'cred.catalogue.availability': "Availability",
  'cred.catalogue.validated': "Validated versions",
  'cred.catalogue.limitations': "Limitations",
  'cred.catalogue.development': "development only",
  'cred.catalogue.unavailable': "unavailable",
  'cred.catalogue.simulator': "Simulator for development and tests; refused in production.",
  'cred.catalogue.unvalidated': "No validated adapter: needs a vendor sandbox and compatibility tests (open decision H-05).",
} as const;
